import { Link, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { useState } from "react";
import { Button } from "../../../../ui/components/Button";
import { Card } from "../../../../ui/components/Card";
import { Callout } from "../../../../ui/components/Callout";
import { AuthBrand } from "../../../../ui/components/AuthBrand";
import { OrDivider } from "../../../../ui/components/OrDivider";
import { GoogleIcon } from "../../../../ui/components/Auth/GoogleIcon";
import * as authText from "../../../../ui/components/Auth/authTypography.style.css";
import { EmailInput, PasswordInput } from "../../../../ui/components/Form";
import { useAuth } from "../../../lib/auth/hooks";
import { persistInviteToken } from "../../../lib/auth/invite-pending";
import {
  getPendingExternalRedirectApp,
  isAllowedExternalRedirect,
  prefetchReturnDestination,
  returnAfterAuth,
} from "../../../lib/auth/external-redirect";
import { useI18n } from "../../../lib/i18n";
import { SmartLinkOnboardingCard } from "../../auth/SmartLinkOnboardingCard";
import type { SmartLinkOnboardingResponse } from "../../../lib/api/types";
import * as styles from "./LoginPage.style.css";

export interface LoginPageProps {
  redirect?: string;
}

function isCertificationRedirect(redirect?: string): boolean {
  if (getPendingExternalRedirectApp() === "certification") return true;
  if (!redirect) return false;
  try {
    if (/^https?:\/\//i.test(redirect) && isAllowedExternalRedirect(redirect)) {
      return /certification/i.test(new URL(redirect).hostname);
    }
  } catch {
    // ignore
  }
  return false;
}

function CertProgressBanner({
  className,
}: {
  className: string;
}) {
  const { tx } = useI18n();
  return (
    <div className={className} role="status">
      <p className={styles.progressBannerTitle}>
        {tx("account.login.certProgressBannerTitle")}
      </p>
      <p className={styles.progressBannerText}>
        {tx("account.login.certProgressBannerBody")}
      </p>
    </div>
  );
}

export function LoginPage({ redirect }: LoginPageProps) {
  const navigate = useNavigate();
  const { login, loginWithGoogle } = useAuth();
  const { t, tx } = useI18n();
  const isCertHandoff = isCertificationRedirect(redirect);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [smartLinkOnboarding, setSmartLinkOnboarding] =
    useState<SmartLinkOnboardingResponse | null>(null);

  React.useEffect(() => {
    // Warm pro/cert while the login form is on screen (helps email + Google paths).
    prefetchReturnDestination();

    if (!redirect || typeof window === "undefined") return;
    try {
      const url = new URL(redirect, window.location.origin);
      if (url.pathname === "/accept-invite") {
        const token = url.searchParams.get("token");
        if (token) persistInviteToken(token);
      }
    } catch {
      // ignore malformed redirect
    }
  }, [redirect]);

  const goToDestination = () => {
    if (!returnAfterAuth()) {
      const target = redirect && redirect.startsWith("/") ? redirect : "/";
      navigate({ to: target, replace: true });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      goToDestination();
    } else if (result.needsSmartLinkOnboarding) {
      setSmartLinkOnboarding(result.needsSmartLinkOnboarding);
    } else {
      setError(result.error || t("account.login.incorrectCredentials"));
    }

    setLoading(false);
  };

  const handleGoogle = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      await loginWithGoogle();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("account.login.googleSignInError"));
      setGoogleLoading(false);
    }
  };

  return (
    <div className={styles.stack}>
      {isCertHandoff && (
        <CertProgressBanner className={styles.progressBannerExternal} />
      )}

      <Card className={authText.authCardScroll}>
        <AuthBrand />

        {isCertHandoff && (
          <CertProgressBanner className={styles.progressBannerInternal} />
        )}

        {smartLinkOnboarding ? (
          <SmartLinkOnboardingCard
            onboarding={smartLinkOnboarding}
            onLinked={goToDestination}
            onCancel={() => setSmartLinkOnboarding(null)}
          />
        ) : (
          <>
            <h1 className={authText.titleLg}>
              {tx(isCertHandoff ? "account.login.certTitle" : "account.login.title")}
            </h1>
            <p className={authText.subtitleCentered}>
              {tx(
                isCertHandoff
                  ? "account.login.certSubtitle"
                  : "account.login.subtitle",
              )}
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
              <Button
                variant="outline"
                color="neutral"
                fullWidth
                iconLeft={<GoogleIcon />}
                onClick={handleGoogle}
                loading={googleLoading}
                disabled={loading}
              >
                {tx("account.login.continueWithGoogle")}
              </Button>
            </div>

            <OrDivider />

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 20 }}>
              {error && <Callout variant="error">{error}</Callout>}

              <EmailInput
                label={t("account.common.emailLabel")}
                placeholder={t("account.common.emailPlaceholder")}
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <PasswordInput
                label={t("account.common.passwordLabel")}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />

              <Button type="submit" color="primary" variant="solid" fullWidth size="lg" loading={loading} disabled={googleLoading}>
                {tx("account.login.signIn")}
              </Button>
            </form>

            <p className={authText.footer} style={{ marginTop: 12, marginBottom: 0 }}>
              <Link to="/forgot-password" className={authText.link}>
                {tx("account.login.forgotPassword")}
              </Link>
            </p>

            <p className={authText.footer}>
              {tx("account.login.noAccount")}{" "}
              <Link
                to="/register"
                search={{ email: undefined, redirect }}
                className={authText.link}
              >
                {tx("account.login.createAccount")}
              </Link>
            </p>
          </>
        )}
      </Card>
    </div>
  );
}
